// eslint-disable-next-line import/no-named-as-default
import { Icon } from "@mdi/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { mdiChevronDown } from "@mdi/js";
import {
  Alert,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
  TreeSelect
} from "antd";
import { EVA_RESEARCH_NAME } from "constants/app.constants";
import _isEqual from "lodash/isEqual";
import moment from "moment";
import { setIsUpdateGeoCalculationJobModalOpen } from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { useLengthConversion } from "hooks";

import { getValidWells } from "api/data";
import { getGroupedPlayZonesByPeriod } from "api/map";

import { useUpdateGeoCalculation } from "../hooks";
import {
  getPrimarySourceDisplayName,
  useSurveyDataSources
} from "../hooks/useSurveyDataSources";
import { ScheduleFrequency } from "../models/GeoCalculations";
import { formatWellListForTextArea, getWellListFromTextArea } from "../utils";
import { convertToTreeData } from "./CreateGeoCalculationJobModal";

const { TextArea } = Input;

export const UpdateGeoCalculationJobModal = () => {
  const dispatch = useDispatch();

  const isUpdateGeoCalculationJobModalOpen = useSelector(
    (state: RootState) => state.organizationSettings.isUpdateGeoCalculationJobModalOpen
  );

  const selectedGeoCalculationJob = useSelector(
    (state: RootState) => state.organizationSettings?.selectedGeoCalculationJob
  );

  const [form] = Form.useForm();
  const [includeSampled3D, setIncludeSampled3D] = useState(true);
  const [includeXda, setIncludeXda] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);

  const updateGeoCalculation = useUpdateGeoCalculation();
  const [errors, setErrors] = useState<string>("");
  const [periodKeys, setPeriodKeys] = useState([]);
  const [selectedIpdbZones, setSelectedIpdbZones] = useState<string[]>([]);
  const [treeData, setTreeData] = useState([]);

  const { convert, getDisplayUnit } = useLengthConversion();

  const isSavedFilterJob = !!selectedGeoCalculationJob?.savedFilterId;
  const savedFilterName =
    selectedGeoCalculationJob?.savedFilterName ??
    selectedGeoCalculationJob?.savedFilterId ??
    "";

  // Set the form values to the selected geo job when the modal is opened
  useEffect(() => {
    if (form && isUpdateGeoCalculationJobModalOpen) {
      // Reset form first to clear any stale values from previous edits
      form.resetFields();

      const getScheduleStartDate = () => {
        if (!selectedGeoCalculationJob?.scheduleStartDate) return undefined;

        // Handle ISO string format
        if (typeof selectedGeoCalculationJob.scheduleStartDate === "string") {
          return moment(selectedGeoCalculationJob.scheduleStartDate);
        }

        // Handle number format (seconds)
        if (typeof selectedGeoCalculationJob.scheduleStartDate === "number") {
          return moment(selectedGeoCalculationJob.scheduleStartDate * 1000);
        }

        // Handle MongoDB format
        if (
          typeof selectedGeoCalculationJob.scheduleStartDate === "object" &&
          "$date" in selectedGeoCalculationJob.scheduleStartDate
        ) {
          return selectedGeoCalculationJob.scheduleStartDate.$date?.$numberLong
            ? moment(
                parseInt(selectedGeoCalculationJob.scheduleStartDate.$date.$numberLong)
              )
            : undefined;
        }

        return undefined;
      };

      const formValues = {
        ...selectedGeoCalculationJob,
        leftWidth:
          selectedGeoCalculationJob?.leftWidth != null
            ? convert(selectedGeoCalculationJob.leftWidth, 2)
            : undefined,
        rightWidth:
          selectedGeoCalculationJob?.rightWidth != null
            ? convert(selectedGeoCalculationJob.rightWidth, 2)
            : undefined,
        upHeight:
          selectedGeoCalculationJob?.upHeight != null
            ? convert(selectedGeoCalculationJob.upHeight, 2)
            : undefined,
        downHeight:
          selectedGeoCalculationJob?.downHeight != null
            ? convert(selectedGeoCalculationJob.downHeight, 2)
            : undefined,
        wellList: formatWellListForTextArea(selectedGeoCalculationJob?.wellList),
        zones: selectedGeoCalculationJob?.zones?.map((z, idx) => `${idx}_map_${z}`) || [],
        scheduleStartDate: getScheduleStartDate()
      };
      form.setFieldsValue(formValues);
      setIncludeSampled3D(formValues.includeSampled3D ?? true);
      setIncludeXda(formValues.includeXda ?? false);
      setIsScheduled(formValues.isScheduled ?? false);
    }
    setModelSource(selectedGeoCalculationJob?.modelSource || "");
    setPrimarySource(selectedGeoCalculationJob?.primarySource);
  }, [selectedGeoCalculationJob, form, isUpdateGeoCalculationJobModalOpen]);

  useEffect(() => {
    if (updateGeoCalculation.isSuccess) {
      dispatch(setIsUpdateGeoCalculationJobModalOpen(false));
    }
  }, [updateGeoCalculation.isSuccess]);

  const [modelSource, setModelSource] = useState("");
  const [primarySource, setPrimarySource] = useState<string | undefined>(undefined);
  const surveySources = useSurveyDataSources();
  const primarySourceLabel = getPrimarySourceDisplayName(primarySource, surveySources);

  useEffect(() => {
    if (modelSource === "") {
      getGroupedPlayZonesByPeriod().then((response) => {
        const treeData = convertToTreeData(response.data) ?? [];
        setPeriodKeys(response.data.map((p) => `period_${p.period}`));
        setTreeData(treeData);
      });
    } else {
      // Can't change zones to we don't really need to load full map layers, just the selected ones
      const zones = selectedGeoCalculationJob?.zones || [];
      const treeData = zones.map((zone, idx) => ({
        title: zone,
        value: `${idx}_map_${zone}`,
        key: `${idx}_map_${zone}`,
        type: `zone`,
        checkable: false
      }));
      setTreeData(treeData);
    }
  }, [modelSource]);

  // Limited to updating the well list (or scheduling) for now
  async function handleOk() {
    const values = form.getFieldsValue();

    let wellList = selectedGeoCalculationJob?.wellList ?? [];
    if (!isSavedFilterJob) {
      const rawList = getWellListFromTextArea(values.wellList);
      wellList = await getValidWells(rawList);
      if (wellList.length === 0) {
        setErrors("0 valid wells in the well list.");
        return;
      }
    }

    if (values.isScheduled && (!values.scheduleFrequency || !values.scheduleStartDate)) {
      setErrors(
        "Schedule frequency and start date are required when scheduling is enabled."
      );
      return;
    }

    // Check if anything has changed
    const wellListChanged =
      !isSavedFilterJob && !_isEqual(selectedGeoCalculationJob.wellList, wellList);
    const isScheduledChanged =
      values.isScheduled !== selectedGeoCalculationJob.isScheduled;
    const scheduleFrequencyChanged =
      values.scheduleFrequency !== selectedGeoCalculationJob.scheduleFrequency;

    // Compare schedule start dates (handle MongoDB format, number format, and ISO string format)
    const getExistingScheduleStartDateMs = () => {
      if (!selectedGeoCalculationJob.scheduleStartDate) return null;
      if (typeof selectedGeoCalculationJob.scheduleStartDate === "string") {
        return new Date(selectedGeoCalculationJob.scheduleStartDate).getTime();
      }
      if (typeof selectedGeoCalculationJob.scheduleStartDate === "number") {
        return selectedGeoCalculationJob.scheduleStartDate * 1000; // Convert seconds to milliseconds
      }
      if (
        typeof selectedGeoCalculationJob.scheduleStartDate === "object" &&
        "$date" in selectedGeoCalculationJob.scheduleStartDate
      ) {
        return selectedGeoCalculationJob.scheduleStartDate.$date?.$numberLong
          ? parseInt(selectedGeoCalculationJob.scheduleStartDate.$date.$numberLong)
          : null;
      }
      return null;
    };

    const existingDateMs = getExistingScheduleStartDateMs();
    const newDateMs = values.scheduleStartDate
      ? values.scheduleStartDate.valueOf()
      : null;
    const scheduleStartDateChanged = existingDateMs !== newDateMs;

    const hasChanges =
      wellListChanged ||
      isScheduledChanged ||
      scheduleFrequencyChanged ||
      scheduleStartDateChanged;

    // If nothing changed, close the modal
    if (!hasChanges) {
      handleClose();
      return;
    }

    const scheduleStartDateTimestamp =
      values.isScheduled && values.scheduleStartDate
        ? {
            $date: {
              $numberLong: values.scheduleStartDate
                .startOf("day")
                .add(1, "minute")
                .valueOf()
                .toString()
            }
          }
        : null;

    // Handle nextScheduledRun based on scheduling state
    let nextScheduledRunValue = null;
    if (!values.isScheduled) {
      // When scheduling is turned off, clear nextScheduledRun
      nextScheduledRunValue = null;
    } else if (scheduleStartDateChanged) {
      // When date changed and scheduling is on, use new date
      nextScheduledRunValue = scheduleStartDateTimestamp;
    } else {
      // When date hasn't changed and scheduling is on, keep existing value
      // Convert existing nextScheduledRun to MongoDB format (handle all formats)
      if (selectedGeoCalculationJob.nextScheduledRun) {
        // Already in MongoDB format
        if (
          typeof selectedGeoCalculationJob.nextScheduledRun === "object" &&
          "$date" in selectedGeoCalculationJob.nextScheduledRun
        ) {
          nextScheduledRunValue = selectedGeoCalculationJob.nextScheduledRun;
        } else if (typeof selectedGeoCalculationJob.nextScheduledRun === "string") {
          // ISO string format - convert to MongoDB format
          const timestamp = new Date(
            selectedGeoCalculationJob.nextScheduledRun
          ).getTime();
          nextScheduledRunValue = { $date: { $numberLong: timestamp.toString() } };
        } else if (typeof selectedGeoCalculationJob.nextScheduledRun === "number") {
          // Number format (seconds) - convert to MongoDB format
          const timestamp = selectedGeoCalculationJob.nextScheduledRun * 1000;
          nextScheduledRunValue = { $date: { $numberLong: timestamp.toString() } };
        }
      }
    }

    updateGeoCalculation
      .mutateAsync({
        ...selectedGeoCalculationJob,
        wellList,
        isScheduled: values.isScheduled ?? false,
        scheduleFrequency: values.isScheduled ? values.scheduleFrequency : null,
        scheduleStartDate: scheduleStartDateTimestamp,
        nextScheduledRun: nextScheduledRunValue
      })
      .then(() => {
        setErrors("");
        handleClose();
      })
      .catch((error) => {
        setErrors(error?.response?.data || "Error occurred while updating job");
      });
  }

  const handleClose = () => {
    updateGeoCalculation.reset();
    setErrors("");
    dispatch(setIsUpdateGeoCalculationJobModalOpen(false));
  };

  const requiredRule = { required: true, message: "This field is required" };

  return (
    <RootContainer>
      <Modal
        closable={false}
        confirmLoading={updateGeoCalculation.isLoading}
        destroyOnClose={true}
        okButtonProps={{ loading: updateGeoCalculation.isLoading, className: "" }}
        maskClosable={false}
        onCancel={handleClose}
        onOk={handleOk}
        open={isUpdateGeoCalculationJobModalOpen}
        title={"Update Geo Calculation Job"}
        width={"700px"}>
        <Form
          preserve={false}
          form={form}
          initialValues={{
            ...selectedGeoCalculationJob,
            wellList: formatWellListForTextArea(selectedGeoCalculationJob?.wellList)
          }}
          name="edit"
          onFinish={handleOk}
          requiredMark={false}>
          <Form.Item name="name" label={<Label>Name</Label>} rules={[requiredRule]}>
            <Input disabled={true} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="leftWidth"
                label={<Label>Left Width ({getDisplayUnit()})</Label>}
                rules={[requiredRule]}>
                <InputNumber disabled={true} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="rightWidth"
                label={<Label>Right Width ({getDisplayUnit()})</Label>}
                rules={[requiredRule]}>
                <InputNumber disabled={true} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="upHeight"
                label={<Label>Up Height ({getDisplayUnit()})</Label>}
                rules={[requiredRule]}>
                <InputNumber disabled={true} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="downHeight"
                label={<Label>Down Height ({getDisplayUnit()})</Label>}
                rules={[requiredRule]}>
                <InputNumber disabled={true} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              {isSavedFilterJob ? (
                <Form.Item label={<Label>Saved Filter</Label>}>
                  <Input
                    disabled={true}
                    data-testid="saved-filter-name-display"
                    value={savedFilterName}
                  />
                </Form.Item>
              ) : (
                <Form.Item name="wellList" label={<Label>Well List</Label>}>
                  <TextArea autoSize={{ minRows: 15, maxRows: 15 }} />
                </Form.Item>
              )}
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label={<Label>Survey Source</Label>}>
                <Select
                  disabled={true}
                  value={primarySource || "gdcDB"}
                  options={[
                    { label: primarySourceLabel, value: primarySource || "gdcDB" }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="modelSource" label={<Label>Geo Source</Label>}>
                <Select
                  data-testid="model-source-select"
                  disabled={true}
                  value={modelSource}
                  options={[{ label: EVA_RESEARCH_NAME, value: "" }].concat(
                    modelSource ? [{ label: modelSource, value: modelSource }] : []
                  )}
                  onChange={(e) => {
                    setModelSource(e);
                    setSelectedIpdbZones([]);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="zones" label={<Label>Zones</Label>}>
                <TreeSelect
                  disabled={true}
                  showSearch
                  suffixIcon={<Icon path={mdiChevronDown} size={1} />}
                  switcherIcon={
                    <SwitcherIcon>{<Icon path={mdiChevronDown} size={1} />}</SwitcherIcon>
                  }
                  data-testid="geo-zone-select"
                  value={selectedIpdbZones}
                  dropdownStyle={{ overflow: "auto" }}
                  placeholder="Please select"
                  allowClear
                  multiple
                  maxTagCount={10}
                  onChange={setSelectedIpdbZones}
                  treeDefaultExpandedKeys={periodKeys}
                  treeCheckable={true}
                  treeData={treeData}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="includeSampled3D" label={<Label>Sampled 3D</Label>}>
                <Switch
                  disabled={true}
                  checked={includeSampled3D}
                  onChange={setIncludeSampled3D}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="includeXda" label={<Label>XDA</Label>}>
                <Switch disabled={true} checked={includeXda} onChange={setIncludeXda} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="isScheduled"
                label={<Label>Schedule Job</Label>}
                valuePropName="checked">
                <Switch
                  checked={isScheduled}
                  onChange={(checked) => {
                    setIsScheduled(checked);
                    form.setFieldValue("isScheduled", checked);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          {isScheduled && (
            <>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="scheduleFrequency"
                    label={<Label>Frequency</Label>}
                    rules={[
                      { required: isScheduled, message: "This field is required" }
                    ]}>
                    <Select
                      placeholder="Select frequency"
                      options={[
                        { label: "Weekly", value: ScheduleFrequency.Weekly },
                        { label: "Monthly", value: ScheduleFrequency.Monthly }
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="scheduleStartDate"
                    label={<Label>Start Date</Label>}
                    rules={[
                      { required: isScheduled, message: "This field is required" }
                    ]}>
                    <DatePicker
                      style={{ width: "100%" }}
                      format="YYYY-MM-DD"
                      disabledDate={(current) =>
                        current && current < moment().startOf("day")
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>

        {(updateGeoCalculation.isError || errors) && (
          <ErrorMessage
            type="error"
            message={`${errors || "Error occurred while creating job"}`}
          />
        )}
      </Modal>
    </RootContainer>
  );
};

const RootContainer = styled.div``;

const Label = styled.span`
  display: block;
  width: 110px;
`;

const SwitcherIcon = styled.span`
  transform: translateY(2px);
  color: rgba(var(--color-text-rgb), 0.3);
`;

const ErrorMessage = styled(Alert)`
  margin-bottom: 1em;
`;
